/**
 * UNI-Q129 — 파티 칸(Q276 부터 메뉴 › 사람 › 파티 한 곳 — 전에는 거래 탭 맨 위에도 있었다). 서버가 `you.party` 로 싣는 것만 그린다 — 경험치 분배·우선권 판정은 서버 몫이다.
 *
 * 규칙(사용자 확정 2026-09-15): 최대 4명 · 경험치 균등 분배 + 인원당 +10% · 전리품 순번제(드롭마다 차례로 우선권).
 * 초대는 보이는 사람 목록 또는 이름으로. 받은 초대는 여기서 수락·거절한다(덮개를 띄우지 않는다 — 거래 요청과 같은 원칙).
 */
import { S, esc } from '../../core/state.js';
import { on } from '../../core/net.js';
import { toast } from '../toast.js';
import { SFX } from '../../core/sound.js';
import { fold, isResident, muted, num, sec } from './common.js';
import { levelLabel } from '../../core/level-label.js';
import { starMark } from '../star.js';

on('partyInviteAsk', (m) => {
  // R52(2.0 런칭 QA, DESIGN 14.342) — 서버 로그 줄을 없앴으니(중복) 남은 시간을
  // 여기 토스트 문구에 싣는다. 안 그러면 몇 초 안에 수락해야 하는지가 안 보인다.
  const ttl = Number(m.ttl) > 0 ? `${Number(m.ttl)}초 안에 ` : '';
  // R227·R220 → Q276 — 파티 창은 메뉴 › 사람 › 파티 한 곳이다(처음 온 사람의 짧은 메뉴에도 보인다)
  toast(`${m.from.name} 님이 파티에 초대했다. 메뉴 › 사람 › 파티에서 ${ttl}수락할 수 있다.`, 'trade', {
    label: '초대 보기', action: () => window.dispatchEvent(new CustomEvent('unige:panel', { detail: 'party' })),
  });
  SFX.shard?.();
  window.dispatchEvent(new CustomEvent('unige:trade'));
});

export function partySection(ctx) {
  const party = ctx.y?.party;
  if (!party) return '';
  if (!party.on) return sec('파티', muted('지금은 파티 기능이 꺼져 있다.'));
  let h = '';
  if (party.invite) {
    h += `<section class="ux-hero"><p class="ux-kicker">받은 초대</p><h3 class="ux-title">${esc(party.invite.name)} 님과 함께하기</h3><p class="ux-summary">Lv.${levelLabel(party.invite.level)} · 초대가 만료되기 전에 수락할 수 있다.</p>`
      + `<div class="ux-actions"><button class="ux-primary" onclick="act.partyAccept('${esc(party.invite.id)}')">파티에 합류하기</button>`
      + `<button onclick="act.partyDecline()">초대 거절</button></div></section>`;
  }
  const me = ctx.y;
  const leader = party.leader === me.id;
  if (party.members.length) {
    h += '<div class="ux-facts"><div class="ux-fact"><span>함께하는 사람</span><b><span class="ux-number">' + party.members.length + '/' + party.max + '</span><small>명</small></b></div><div class="ux-fact"><span>경험치를 함께 받는 거리</span><b><span class="ux-number">' + num(party.shareRadius) + '</span><small>타일 안 · 같은 층</small></b></div></div>';
    h += party.members.map((m) => {
      const off = m.away ? `접속 끊김 · ${num(m.away)}초 뒤 빠짐` : '접속 끊김';
      const place = !m.online ? off : m.location === 'town' ? '마을 안 — 경험치 몫 없음' : m.location === 'other' ? '다른 사냥터'
        : m.location === 'stale' ? '위치 확인 중' : m.near ? '곁에 있음' : '멀리 있음';
      const tags = [m.id === party.leader ? '파티장' : '', place].filter(Boolean).join(' · ');
      const kick = leader && m.id !== party.leader ? ` <button class="ghost" onclick="act.partyKick('${esc(m.id)}')">추방</button>` : '';
      const hp = m.hp == null ? 'HP 확인 중' : `HP ${num(m.hp)}%`;
      return `<div class="kv"><span><b>${starMark(m.name)}${esc(m.name)}</b> <em>Lv.${levelLabel(m.level)}</em><br><span class="qt">${tags}</span></span><b>${hp}${kick}</b></div>`;
    }).join('')
      + '<p class="ux-note">파티 채팅은 채팅창에서 /p 로 시작한다.</p>';
  } else if (!party.invite) {
    h += '<section class="ux-hero"><p class="ux-kicker">함께 떠나는 사냥</p><h3 class="ux-title">아직 혼자다</h3><p class="ux-summary">곁에 있는 사람이나 이름으로 동료를 초대하자. 상대가 수락하면 파티가 된다.</p></section>';
  }
  const full = party.members.length >= party.max;
  const canInvite = !party.members.length || leader;
  if (canInvite && !full) {
    const mine = new Set(party.members.map((m) => m.id));
    // R257 — 원주민은 아직 초대할 수 없다(아래 안내). 사냥터에서 소속 카드가 늦어도 몸 id 로 걸러 사람처럼 뜨지 않게 한다
    const near = S.players.filter((o) => !o.d && !isResident(o) && !mine.has(o.id))
      .map((o) => ({ ...o, dist: Math.hypot(o.x - me.x, o.y - me.y) })).sort((a, b) => a.dist - b.dist).slice(0, 8);
    h += '<details class="ux-details" id="party-invite"' + (!party.members.length && !party.invite ? ' open' : '') + '><summary>함께할 사람 초대하기</summary>';
    h += (near.length ? '<div class="row wrap">' + near.map((o) => `<button onclick="act.partyInvite('${esc(o.id)}')">${starMark(o.n)}${esc(o.n)} <em>Lv.${levelLabel(o.lv)}</em> 초대</button>`).join('') + '</div>'
      : muted('보이는 곳에 초대할 사람이 없다. 이름으로 초대할 수 있다.'))
      + `<div class="row"><input id="partyname" placeholder="캐릭터 이름" maxlength="24" aria-label="파티에 초대할 캐릭터 이름"><button onclick="act.partyInviteName()">이름으로 초대</button></div></details>`;
  }
  // Q276 — 규칙은 한 줄 요약 + [자세히](전문은 펼침 안)
  h += fold(`최대 ${num(party.max)}명 · 경험치를 나누고 한 명마다 +${num(party.bonusPct)}%`,
    `최대 ${num(party.max)}명. 같은 층 ${num(party.shareRadius)}타일 안에 살아 있는 파티원이 처치 경험치를 <b>똑같이 나누고</b>, 한 명 늘 때마다 총 경험치가 <b>+${num(party.bonusPct)}%</b>. `
    + '파티원이 잡은 몬스터의 전리품은 <b>드롭마다 차례로</b> 한 사람에게 잠깐 우선권이 간다(흐린 물건은 남의 차례). '
    + `파티 채팅은 채팅창에서 <b>/p</b> 로 시작한다. 접속이 끊겨도 ${num(party.grace)}초 안에 돌아오면 파티에 그대로 남는다. 원주민은 아직 초대할 수 없다.`);
  if (party.members.length) h += '<details class="ux-details" id="party-leave"><summary>파티 떠나기</summary>' + `<button onclick="act.partyLeave()">파티 떠나기</button></details>`;
  return sec(party.members.length ? '내 파티' : '파티', h);
}
